import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useQueryParams from 'hooks/useQueryParams';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  DashboardMetricColumns,
  DashboardMetricContentTransformations,
  DEAL_SORTING_VALUES,
  CUSTOM_COL_SIZES,
  HeadingTranslations,
} from './data';
import { Button } from 'ncoded-component-library';
import { TIMEFRAME_PARAM_KEYWORD } from 'data/global';
import { TIMEFRAME } from 'dateUtils';
import TableIcon from 'icons/Table.icon';
import utils, {
  checkIsPublished,
  downloadUrlInNewWindow,
  sortOptions,
} from 'utils';
import { DashboardMetric } from './types';
import { useNavigate } from 'react-router-dom';
import useCallbackRef from 'hooks/useCallbackRef';
import PaymentInvoiceSkipped from './components/PaymentInvoiceSkipped';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import MultiFilter from 'components/MultiFilter';
import { FilterOption } from 'models/Filters';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import useGetFilterOptions from 'hooks/useGetFilterOptions';

import './ListingMetrics.styles.scss';

const RELATIONS = '["listing","dealRoom","user"]';

const HIDE_METRICS_FILTERS_PARAM = 'hideMetrics';

const BROKER_FILTER_KEY = 'brokerName';
const LISTING_FILTER_KEY = 'listingName';

const FILTER_COPY = {
  hideResolveManagedListings: 'isResolveManagedListing',
  hidePausedListings: 'isPausedListing',
  hideEndedListings: 'isEndedListing',
};

type ListingMetricsProps = {
  className?: string;
};

const ListingMetrics: React.FC<ListingMetricsProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { popServerError } = useContext(PopNotificationsContext);

  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const [dealMetrics, setDealMetrics] = useState<DashboardMetric[]>([]);

  const [dashboardMetric, setDashboardMetric] = useState<DashboardMetric>();

  const [bmResponse, bmResponseRef] = useCallbackRef<ModalRef>();

  const { params } = useQueryParams();

  const { sort, page, timeframe, hideMetrics, brokerName, listingName } =
    params;

  const classes = classNames('listing-metrics', className);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const createSelectOptions = useCreateSelectOptions();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const navigate = useNavigate();

  const hideMetricsFilter = useMemo(
    () =>
      hideMetrics
        ? utils.mapParamsToArray(hideMetrics).reduce(
            (acc, filterKey) => ({
              ...acc,
              [filterKey]: false,
            }),
            {},
          )
        : {},
    [hideMetrics],
  );

  const handleOnRowClick = useCallback(
    ({ listing }: DashboardMetric) => {
      if (!listing) return;

      const { industry, slugName, type, stage } = listing;

      const isPublished = checkIsPublished(stage);

      if (!isPublished || !industry || !slugName || !type) return;

      const redirectionListingLink = utils.composeBusinessUrl(
        industry,
        slugName,
        type,
      );

      navigate(redirectionListingLink);
    },
    [navigate],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.portfolio.admin.getDashboardMetrics({
          $sort: sort,
          $page: page,
          $limit: limit,
          $relations: RELATIONS,
          brokerName,
          listingName,
          timeframe,
          ...hideMetricsFilter,
        });
        setDealMetrics(items);
        setTotalPages(totalPages);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    page,
    limit,
    brokerName,
    listingName,
    sort,
    timeframe,
    hideMetricsFilter,
    popServerError,
  ]);

  const getDashboardMetricsBrokerOptions = useCallback(
    async (searchValue: string) => {
      const {
        data: { brokers },
      } = await api.portfolio.admin.getBrokerSearchvalues(searchValue);

      return sortOptions(
        brokers.map((value) => ({
          value,
          label: value,
        })),
      );
    },
    [],
  );

  const getDashboardMetricsListingOptions = useCallback(
    async (searchValue: string) => {
      const {
        data: { listings },
      } = await api.portfolio.admin.getListingSearchvalues(searchValue);

      return sortOptions(
        listings.map((value) => ({
          value,
          label: value,
        })),
      );
    },
    [],
  );

  const updateDashboardMetric = useCallback(
    (dashboardMetric: Partial<DashboardMetric>) =>
      setDealMetrics((old) =>
        old.map((el) =>
          el._id === dashboardMetric._id ? { ...el, ...dashboardMetric } : el,
        ),
      ),
    [],
  );

  const openInvoiceModal = useCallback(
    (metric: DashboardMetric) => {
      setDashboardMetric(metric);
      bmResponse?.open();
    },
    [bmResponse],
  );

  const renderedContent = useMemo(
    () =>
      DashboardMetricColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (dashboardMetric: DashboardMetric) =>
            DashboardMetricContentTransformations[col](
              dashboardMetric,
              openInvoiceModal,
            ),
        }),
        {},
      ),
    [openInvoiceModal],
  );

  const filterOptions = useMemo(
    () =>
      [
        {
          value: HIDE_METRICS_FILTERS_PARAM,
          label: t('hideMetrics'),
          isMultiple: true,
          isSearchable: false,
          shouldSortOptions: false,
          options: createSelectOptions(FILTER_COPY),
        },
        getMultiSelectSearchableFilter(
          t('broker'),
          BROKER_FILTER_KEY,
          BROKER_FILTER_KEY,
          getDashboardMetricsBrokerOptions,
        ),
        getMultiSelectSearchableFilter(
          t('listing'),
          LISTING_FILTER_KEY,
          LISTING_FILTER_KEY,
          getDashboardMetricsListingOptions,
        ),
        {
          value: TIMEFRAME_PARAM_KEYWORD,
          label: t('publishedDuring'),
          shouldSortOptions: false,
          options: createSelectOptions(Object.values(TIMEFRAME)),
        },
      ] as FilterOption[],
    [
      createSelectOptions,
      getDashboardMetricsBrokerOptions,
      getDashboardMetricsListingOptions,
      getMultiSelectSearchableFilter,
      t,
    ],
  );

  const downloadCSV = useCallback(async () => {
    try {
      setIsDownloading(true);

      const {
        data: { url, type },
      } = await api.portfolio.admin.getDashboardMetricsCsv();

      if (url) downloadUrlInNewWindow(url, type);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDownloading(false);
    }
  }, [popServerError]);

  return (
    <div className={classes}>
      <div className="listing-metrics__heading">
        <h1>{t('listingMetrics')}</h1>

        <div className="listing-metrics__heading__searching-cont">
          <NumberOfResultsPerPageFilter
            limit={limit}
            updateLimit={updateLimit}
          />

          <Button
            className="export-button"
            icon={TableIcon}
            disabled={isDownloading}
            onClick={downloadCSV}
          >
            {t('logCsv')}
          </Button>

          <MultiFilter filterOptions={filterOptions} />
        </div>
      </div>

      <ReorderResizeTable
        className={classes}
        isDragDisabled
        columns={DashboardMetricColumns}
        data={dealMetrics}
        totalPages={totalPages}
        isDataLoading={isLoading}
        sortingValues={DEAL_SORTING_VALUES}
        customSizes={CUSTOM_COL_SIZES}
        headingTranslations={HeadingTranslations}
        tableContentTransformations={renderedContent}
        onRowClick={handleOnRowClick}
      />

      <PaymentInvoiceSkipped
        ref={bmResponseRef}
        dashboardMetric={dashboardMetric}
        updateDashboardMetric={updateDashboardMetric}
      />
    </div>
  );
};

export default ListingMetrics;
